<template>
  <modal :name="nombreModal" :adaptive="true" height="auto" width="560px">
    <div class="conf">
      <div class="modal-header conf-cabecera">
        <h4>{{ $t("reindexarTitulo", { g: grupo }) }}</h4>
      </div>

      <div class="modal-body conf-cuerpo">
        <!-- Lo que va a pasar, antes de pedir nada. Mismo orden que los modales de baja:
             primero el precio, luego la decision. -->
        <ul class="conf-consecuencias">
          <li>{{ $tc("reindexarQuitaHuecos", huecos, { n: huecos }) }}</li>
          <li v-if="movimientos.length">
            {{ $tc("reindexarCambiaTecla", movimientos.length, { n: movimientos.length }) }}
          </li>
          <li>{{ $t("reindexarProductosSeQuedan") }}</li>
          <li>{{ $tc("seGuardaEnTerminales", terminales, { n: terminales }) }}</li>
        </ul>

        <!-- Que categoria acaba en que tecla. Es lo que el `swal` de antes no decia: solo
             avisaba de que "se moveran hacia delante", y el camarero se encontraba la
             carta cambiada sin que nadie hubiese podido verlo antes. -->
        <div class="rei-cambios" v-if="movimientos.length">
          <p class="rei-titulo">{{ $t("reindexarComoQueda") }}</p>

          <ul class="rei-lista">
            <li v-for="m in movimientos" :key="'m' + m.desde">
              <span class="rei-nombre">{{ m.nombre }}</span>
              <span class="rei-tecla">{{ m.desde }}</span>
              <span class="rei-flecha">&rarr;</span>
              <span class="rei-tecla nueva">{{ m.hasta }}</span>
            </li>
          </ul>
        </div>

        <p class="conf-nota" v-else>{{ $t("reindexarSinCambios") }}</p>

        <p class="conf-error" v-if="error">{{ error }}</p>
      </div>

      <div class="modal-footer conf-pie">
        <button type="button" class="btn btn-claro" :disabled="ocupado" @click="cerrar()">
          {{ $t("cancelar") }}
        </button>
        <button
          type="button"
          class="btn btn-verde"
          :disabled="ocupado || !movimientos.length"
          @click="confirmar()"
        >
          {{ $t("reindexar") }}
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
/**
 * Reindexar un grupo de categorias: juntarlas al principio, sin huecos.
 *
 * Sustituye al `swal` que habia antes, que decia cuantos huecos habia y poco mas. Aqui se
 * ensena ADEMAS que categoria acaba en que tecla, que es lo unico que el usuario necesita
 * para decidir: reindexar no borra nada, pero le cambia la carta al camarero de sitio, y
 * eso no se puede deshacer con un boton.
 *
 * Lo que se mueve es el CODE, no el contenido. Una categoria que estaba en la tecla 3 pasa
 * a la 1 si la 1 estaba libre; sus productos siguen siendo suyos y no cambian de categoria.
 * El terminal se entera por el `updated` de `categories` y de `products`.
 *
 * La prevision se calcula aqui y no se pide al servidor porque es exactamente la misma
 * regla -las que tienen nombre, en su orden, a las primeras teclas del grupo- y pedirla
 * seria un viaje para ensenar algo que ya esta en pantalla. Hay una comprobacion que la
 * contrasta contra lo que hace de verdad `reindexBusinessCategories`.
 *
 * Como los demas modales nuevos, este NO escribe: emite `confirmar` con el grupo y la
 * pantalla llama al endpoint.
 */
export default {
  name: "ReindexarGrupoModal",

  props: {
    nombreModal: { type: String, default: "modalReindexarGrupo" },
    // Terminales vivos del negocio, solo para el plural del aviso.
    terminales: { type: Number, default: 0 },
    ocupado: { type: Boolean, default: false }
  },

  data() {
    return {
      grupo: 0,
      // Las categorias del grupo TAL COMO vienen de la API: en orden de tecla y con los
      // huecos incluidos en su sitio. Ese orden ES el dato; sin el no se puede saber a
      // que tecla va cada una.
      categorias: [],
      error: ""
    };
  },

  computed: {
    conNombre() {
      return this.categorias.filter(function(c) {
        return c.nombre !== "";
      });
    },

    huecos() {
      return this.categorias.length - this.conNombre.length;
    },

    /**
     * Que categoria pasa de que tecla a cual, solo las que se mueven.
     *
     * Las teclas de destino son las del propio grupo en su orden: las categorias con
     * nombre van a las primeras y los huecos se van al final. Es la misma regla que
     * aplica el servidor.
     */
    movimientos() {
      let teclas = this.categorias.map(function(c) {
        return c.code;
      });

      return this.conNombre
        .map(function(c, i) {
          return { nombre: c.nombre, desde: c.code, hasta: teclas[i], productos: c.productos };
        })
        .filter(function(m) {
          // Comparacion de cadenas: las teclas son "0".."9" y letras, y "0" es una tecla
          // de verdad -la primera del grupo 1-, asi que nada de comparar por falsy.
          return m.desde !== m.hasta;
        });
    }
  },

  methods: {
    /**
     * @param {number} grupo      1-4.
     * @param {Array}  categorias Las del grupo, en orden de tecla y con sus huecos.
     */
    abrir(grupo, categorias) {
      this.grupo = grupo;
      this.categorias = categorias || [];
      this.error = "";

      this.$modal.show(this.nombreModal);
    },

    cerrar() {
      this.$modal.hide(this.nombreModal);
    },

    mostrarError(mensaje) {
      this.error = mensaje || this.$t("errorInesperado");
    },

    confirmar() {
      if (!this.movimientos.length) {
        return;
      }

      this.error = "";
      this.$emit("confirmar", this.grupo);
    }
  }
};
</script>

<style scoped>
@import "../confirmar-en-modal.css";

/* ---------------------------------------------------------------------------
   Solo de este modal: la prevision de como queda el grupo.
   --------------------------------------------------------------------------- */

.rei-titulo {
    margin: 0 0 8px;
    font-size: 0.94em;
    font-weight: 500;
    color: #2c3e50;
}

.rei-lista {
    margin: 0;
    padding: 0;
    list-style: none;
    max-height: 240px;
    overflow-y: auto;
    border: 1px solid #e6e9ef;
    border-radius: 3px;
}

.rei-lista li {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    border-bottom: 1px solid #f0f2f6;
    font-size: 0.9em;
}

.rei-lista li:last-child {
    border-bottom: 0;
}

.rei-nombre {
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #2c3e50;
}

/* La tecla se pinta como lo que es -una tecla del terminal-, en monoespaciada y con su
   recuadro, para que no se lea como un numero mas de la fila. */
.rei-tecla {
    flex: 0 0 auto;
    min-width: 24px;
    padding: 2px 0;
    text-align: center;
    font-family: Menlo, Consolas, monospace;
    font-size: 0.92em;
    border: 1px solid #dde2e9;
    border-radius: 3px;
    background: #f7f9fb;
    color: #8b96a3;
}

.rei-tecla.nueva {
    border-color: #009982;
    background: rgba(0, 153, 130, 0.08);
    color: #00806d;
    font-weight: 600;
}

.rei-flecha {
    flex: 0 0 auto;
    color: #c3cbd5;
}
</style>



// WEBPACK FOOTER //
// src/pages/Widgets/Categorias/ReindexarGrupoModal.vue